import { Component, OnDestroy, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { IDeactivate } from '../../../common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from '../../../common/long-operations/operation-info.service';
import ProjectLinks from '../../../common/project.links';
import { NotificationPreferencesService } from './notification-preferences.service';
import { ChannelPreferenceByContextName } from './notification_preferences.interfaces';
import { promiseWithCatch, unsubscribeAll } from '../../../common/app.helpers';

@Component({
  selector: 'app-notification-preferences',
  templateUrl: './notification-preferences.component.html',
  styleUrls: ['./notification-preferences.component.scss'],
})
export class NotificationPreferences implements OnInit, OnDestroy, IDeactivate {
  private notificationPreferencesService = inject(NotificationPreferencesService);
  private operationInfoService = inject(OperationInfoService);
  private router = inject(Router);
  private subscriptions$: Subscription[] = [];
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public form: FormGroup<{
    connections: FormGroup<{ emailsEnabled: FormControl<boolean>; messagingEnabled: FormControl<boolean> }>;
    comments: FormGroup<{ emailsEnabled: FormControl<boolean>; messagingEnabled: FormControl<boolean> }>;
    social_group: FormGroup<{ emailsEnabled: FormControl<boolean>; messagingEnabled: FormControl<boolean> }>;
    files: FormGroup<{ emailsEnabled: FormControl<boolean>; messagingEnabled: FormControl<boolean> }>;
  }> = new FormGroup({
    connections: new FormGroup({
      emailsEnabled: new FormControl(true),
      messagingEnabled: new FormControl(true),
    }),
    comments: new FormGroup({
      emailsEnabled: new FormControl(true),
      messagingEnabled: new FormControl(true),
    }),
    social_group: new FormGroup({
      emailsEnabled: new FormControl(true),
      messagingEnabled: new FormControl(true),
    }),
    files: new FormGroup({
      emailsEnabled: new FormControl(true),
      messagingEnabled: new FormControl(true),
    }),
  });

  public isFormChanged = false;
  public allPreferencesSwitchValue = true;
  public initialFormValues: ChannelPreferenceByContextName;
  public initialNotificationContextPreferences: ChannelPreferenceByContextName;

  public ngOnInit(): void {
    this.subscribeOnFormChanges();
    this.subscribeToNotificationPreferencesChanges();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  private subscribeOnFormChanges(): void {
    this.subscriptions$.push(
      this.form.valueChanges.subscribe(() => {
        this.updateIsFormChanged();
        this.updateAllPreferencesSwitchValue();
      }),
    );
  }

  private subscribeToNotificationPreferencesChanges(): void {
    this.subscriptions$.push(
      this.notificationPreferencesService.notificationPreferencesChanged.subscribe(
        (channelPreferenceByContextName: ChannelPreferenceByContextName) => {
          this.initialNotificationContextPreferences = channelPreferenceByContextName;
          this.form.patchValue(channelPreferenceByContextName, { emitEvent: false });
          this.initialFormValues = structuredClone(channelPreferenceByContextName);
          this.updateIsFormChanged();
          this.updateAllPreferencesSwitchValue();

          this.changeDetectorRef.detectChanges();
        },
      ),
    );

    this.notificationPreferencesService.getNotificationPreferences();
  }

  public updateNotificationPreferences(): void {
    const updatedPreferences: ChannelPreferenceByContextName = this.form.getRawValue();
    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        successfulMessage: 'NOTIFICATION_PREFERENCES_WERE_UPDATED',
      },
      () => {
        this.notificationPreferencesService.updateNotificationPreferences(updatedPreferences);
      },
    );
    this.form.markAsUntouched();
    this.form.markAsPristine();

    this.updateIsFormChanged();
  }

  public switchAllPreferences(isChecked: boolean): void {
    this.form.patchValue({
      connections: {
        emailsEnabled: isChecked,
        messagingEnabled: isChecked,
      },
      comments: {
        emailsEnabled: isChecked,
        messagingEnabled: isChecked,
      },
      social_group: {
        emailsEnabled: isChecked,
        messagingEnabled: isChecked,
      },
      files: {
        emailsEnabled: isChecked,
        messagingEnabled: isChecked,
      },
    });
    this.form.markAsDirty();
    this.updateIsFormChanged();
  }

  public discardChanges(): void {
    promiseWithCatch(this.router.navigateByUrl(`${ProjectLinks.LINK_DEFAULT}`));
  }

  private updateAllPreferencesSwitchValue(): void {
    const formValues = this.form.value;
    const switches = [
      formValues.connections.emailsEnabled,
      formValues.connections.messagingEnabled,
      formValues.comments.emailsEnabled,
      formValues.comments.messagingEnabled,
      formValues.social_group.emailsEnabled,
      formValues.social_group.messagingEnabled,
      formValues.files.emailsEnabled,
      formValues.files.messagingEnabled,
    ];

    const trueCount = switches.filter((value: boolean) => value).length;
    this.allPreferencesSwitchValue = trueCount >= switches.length / 2;
  }

  private updateIsFormChanged(): void {
    if (!this.initialFormValues) {
      return;
    }
    this.isFormChanged = this.form.dirty;
  }
}
